import { Injectable, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MediaObserver } from '@angular/flex-layout';

import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

@Pipe({
    name: 'safeHTML',
})
export class SafeHTMLPipe implements PipeTransform {
    constructor(private sanitizer: DomSanitizer) {}

    transform(value: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(value);
    }
}

@Pipe({
    name: 'titleCase',
})
export class TitleCasePipe implements PipeTransform {
    transform(value: string) {
        if (!value) {
            return '';
        }

        return value.replace(/[A-Za-z]+\s*/g, function (val) {
            if (/^[Ii]+$/.test(val)) {
                return val.toUpperCase();
            } else {
                return val[0].toUpperCase() + val.substr(1).toLowerCase();
            }
        });
    }
}

@Injectable()
export class IScreenSizeService implements OnDestroy {
    protected _subrelease: Subscription = new Subscription();
    protected _ScreenSizeSub: BehaviorSubject<ScreenSizeInfo>;

    // #region Getters

    get ScreenSize(): ScreenSizeInfo {
        return this._ScreenSizeSub.value;
    }
    get ScreenSizeSub(): BehaviorSubject<ScreenSizeInfo> {
        return this._ScreenSizeSub;
    }

    // #endregion

    constructor() {
        // Set the initial upkeep
        const info = {
            Alias: '',
            IsSmallDisplay: false,
            IsXs: false,
            IsSm: false,
            IsMd: false,
            IsLg: false,
            IsXl: false,
        } as ScreenSizeInfo;

        info.IsSmallDisplay = info.IsXs || info.IsSm;
        info.Alias = '';

        this._ScreenSizeSub = new BehaviorSubject<ScreenSizeInfo>(info);
    }

    ngOnDestroy(): void {
        this._subrelease.unsubscribe();
        this._ScreenSizeSub.complete();
    }
}

@Injectable()
export class ScreenSizeService extends IScreenSizeService {
    constructor(private mediaService: MediaObserver) {
        super();

        // Set the initial upkeep
        const info = {
            Alias: '',
            IsSmallDisplay: false,
            IsXs: this.mediaService.isActive('xs'),
            IsSm: this.mediaService.isActive('sm'),
            IsMd: this.mediaService.isActive('md'),
            IsLg: this.mediaService.isActive('lg'),
            IsXl: this.mediaService.isActive('xl'),
        } as ScreenSizeInfo;

        info.IsSmallDisplay = info.IsXs || info.IsSm;
        info.Alias = info.IsXs ? 'xs' : info.IsSm ? 'sm' : info.IsMd ? 'md' : info.IsLg ? 'lg' : 'xl';

        this._ScreenSizeSub.next(info);

        // Set up the subscription to keep up changes
        this._subrelease.add(
            this.mediaService
                .asObservable()
                .pipe(debounceTime(250))
                .subscribe((armedia) => {
                    const media = {
                        mqAlias: armedia && !!armedia.length ? armedia[0].mqAlias : '',
                    };

                    const obsinfo = {
                        Alias: media.mqAlias,
                        IsSmallDisplay: media.mqAlias === 'xs' || media.mqAlias === 'sm',
                        IsXs: media.mqAlias === 'xs',
                        IsSm: media.mqAlias === 'sm',
                        IsMd: media.mqAlias === 'md',
                        IsLg: media.mqAlias === 'lg',
                        IsXl: media.mqAlias === 'xl',
                    } as ScreenSizeInfo;

                    this._ScreenSizeSub.next(obsinfo);
                })
        );
    }
}

// #region Custom Classes

// #region Screen Size Custom Query

export interface ScreenSizeCustomQuery {
    Key: string;
    Query: string;
}

// #endregion

// #region Screen Size Info

export interface ScreenSizeInfo {
    Alias: string;
    IsSmallDisplay: boolean;
    IsXs: boolean;
    IsSm: boolean;
    IsMd: boolean;
    IsLg: boolean;
    IsXl: boolean;
}

// #endregion

// #endregion
